import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { Award, Medal, Star, Users, TrendingUp, Share2 } from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { calculateStandings, getGroupStandings, generateBracketStructure } from '../utils/tournamentUtils';

const LeaderboardPage = () => {
  const navigate = useNavigate();
  const { tournament, teams, matches } = useAppContext();
  const [standings, setStandings] = useState([]);
  const [topScorers, setTopScorers] = useState([]);
  const [bracket, setBracket] = useState(null);
  const [activeTab, setActiveTab] = useState('standings');
  
  // Redirect if no tournament
  useEffect(() => {
    if (!tournament) {
      navigate('/');
    }
  }, [tournament, navigate]);
  
  // Calculate standings
  useEffect(() => {
    const calculateLeaderboard = () => {
      // Check if tournament has groups
      const hasGroups = matches.some(match => match.round.startsWith('group-'));
      
      if (hasGroups) {
        // Get all unique group names
        const groupNames = [...new Set(
          matches.filter(match => match.round.startsWith('group-'))
            .map(match => match.round)
        )];
        
        // Get standings for each group
        const groupsStandings = groupNames.map(group => ({
          name: group,
          teams: getGroupStandings(teams, matches, group)
        }));
        
        setStandings(groupsStandings);
      } else {
        // Regular standings
        setStandings(calculateStandings(teams, matches));
      }
      
      // Calculate top scorers (Refactored)
      const playerStats = {};
      // 1. Initialize stats for all players from the main teams list
      teams.forEach(team => {
        team.players.forEach(player => {
          playerStats[player.id] = {
            id: player.id,
            name: player.name,
            teamName: team.name,
            teamColor: team.color,
            goals: 0
          };
        });
      });

      // 2. Process match events to update goal counts
      matches.forEach(match => {
        match.events
          .filter(event => event.type === 'goal' && event.playerId)
          .forEach(event => {
            // Directly increment goal count if player exists in stats
            if (playerStats[event.playerId]) {
              playerStats[event.playerId].goals += 1;
            }
          });
      });
      
      // 3. Convert to array, sort by goals, and take top 10
      const scorersArray = Object.values(playerStats)
        .sort((a, b) => b.goals - a.goals);
      setTopScorers(scorersArray.slice(0, 10)); 
      
      // Generate bracket if knockout matches exist
      const bracketData = generateBracketStructure(matches);
      setBracket(bracketData);
    };
    
    if (teams.length > 0 && matches.length > 0) {
      calculateLeaderboard();
    }
  }, [teams, matches, tournament]);
  
  if (!tournament) return null;
  
  // Render standings table
  const renderStandingsTable = (teamsData) => {
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b dark:border-gray-700">
              <th className="text-left py-2 pl-2 pr-4">#</th>
              <th className="text-left py-2 px-4">Team</th>
              <th className="text-center py-2 px-2">P</th>
              <th className="text-center py-2 px-2">W</th>
              <th className="text-center py-2 px-2">D</th>
              <th className="text-center py-2 px-2">L</th>
              <th className="text-center py-2 px-2">GF</th>
              <th className="text-center py-2 px-2">GA</th>
              <th className="text-center py-2 px-2">GD</th>
              <th className="text-center py-2 px-4">Pts</th>
            </tr>
          </thead>
          <tbody>
            {teamsData.map((team, index) => (
              <tr key={team.id} className="border-b dark:border-gray-700 last:border-0">
                <td className="py-3 pl-2 pr-4">{index + 1}</td>
                <td className="py-3 px-4">
                  <div className="flex items-center">
                    <div 
                      className="w-3 h-3 rounded-full mr-2" 
                      style={{ backgroundColor: team.color }}
                    />
                    <span className="font-medium">{team.name}</span>
                  </div>
                </td>
                <td className="text-center py-3 px-2">{team.played}</td>
                <td className="text-center py-3 px-2">{team.won}</td>
                <td className="text-center py-3 px-2">{team.drawn}</td>
                <td className="text-center py-3 px-2">{team.lost}</td>
                <td className="text-center py-3 px-2 font-medium text-green-600 dark:text-green-400">{team.goalsFor}</td>
                <td className="text-center py-3 px-2 font-medium text-red-600 dark:text-red-400">{team.goalsAgainst}</td>
                <td className={`text-center py-3 px-2 font-medium ${
                  team.goalDifference > 0 
                    ? 'text-green-600 dark:text-green-400' 
                    : team.goalDifference < 0 
                      ? 'text-red-600 dark:text-red-400' 
                      : ''
                }`}>
                  {team.goalDifference > 0 ? `+${team.goalDifference}` : team.goalDifference}
                </td>
                <td className="text-center py-3 px-4 font-bold">{team.points}</td>
              </tr>
            ))}
          </tbody>
        </table>
        
        <div className="text-xs text-gray-500 mt-2 px-2">
          <span className="mr-2">P: Played</span>
          <span className="mr-2">W: Won</span>
          <span className="mr-2">D: Drawn</span>
          <span className="mr-2">L: Lost</span>
          <span className="mr-2">GF: Goals For</span>
          <span className="mr-2">GA: Goals Against</span>
          <span className="mr-2">GD: Goal Difference</span>
          <span>Pts: Points</span>
        </div>
      </div>
    );
  };
  
  return (
    <div className="px-4 py-6 max-w-xl mx-auto">
      <h1 className="text-2xl font-bold mb-6 flex items-center">
        <Award size={24} className="mr-2" />
        Leaderboard
      </h1>
      
      {/* Tabs */}
      <div className="flex space-x-1 mb-4 border-b dark:border-gray-700">
        <button
          className={`py-2 px-4 text-sm font-medium ${
            activeTab === 'standings' 
              ? 'border-b-2 border-blue-500 text-blue-500' 
              : 'text-gray-500 dark:text-gray-400'
          }`}
          onClick={() => setActiveTab('standings')}
        >
          <span className="flex items-center">
            <Users size={16} className="mr-1" />
            Standings
          </span>
        </button>
        
        <button
          className={`py-2 px-4 text-sm font-medium ${
            activeTab === 'scorers' 
              ? 'border-b-2 border-blue-500 text-blue-500' 
              : 'text-gray-500 dark:text-gray-400'
          }`}
          onClick={() => setActiveTab('scorers')}
        >
          <span className="flex items-center">
            <Star size={16} className="mr-1" />
            Top Scorers
          </span>
        </button>
        
        {bracket && (
          <button
            className={`py-2 px-4 text-sm font-medium ${
              activeTab === 'bracket' 
                ? 'border-b-2 border-blue-500 text-blue-500' 
                : 'text-gray-500 dark:text-gray-400'
            }`}
            onClick={() => setActiveTab('bracket')}
          >
            <span className="flex items-center">
              <TrendingUp size={16} className="mr-1" />
              Bracket
            </span>
          </button>
        )}
      </div>
      
      {/* Content based on active tab */}
      {activeTab === 'standings' && (
        <div>
          {Array.isArray(standings) && !standings[0]?.teams ? (
            <Card>
              {renderStandingsTable(standings)}
            </Card>
          ) : (
            Array.isArray(standings) && standings.map(group => (
              <Card key={group.name} className="mb-4">
                <h2 className="text-lg font-semibold mb-3">
                  Group {group.name.replace('group-', '')}
                </h2>
                {renderStandingsTable(group.teams)}
              </Card>
            ))
          )}
        </div>
      )}
      
      {activeTab === 'scorers' && (
        <Card>
          <h2 className="text-lg font-semibold mb-3 flex items-center">
            <Star size={20} className="mr-1" />
            Top Goal Scorers
          </h2>
          
          {topScorers.length === 0 ? (
            <div className="text-center py-6 text-gray-500 dark:text-gray-400">
              No goal scorers yet
            </div>
          ) : (
            <div className="space-y-2">
              {topScorers.map((scorer, index) => (
                <div 
                  key={scorer.id}
                  className="flex items-center py-3 border-b dark:border-gray-700 last:border-0"
                >
                  <div className="w-8 flex justify-center">
                    {index === 0 ? (
                      <Medal size={20} className="text-yellow-500" />
                    ) : index === 1 ? (
                      <Medal size={20} className="text-gray-400" />
                    ) : index === 2 ? (
                      <Medal size={20} className="text-amber-700" />
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-medium">
                        {index + 1}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1 ml-2">
                    <div className="font-medium">{scorer.name}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center">
                      <div 
                        className="w-2 h-2 rounded-full mr-1" 
                        style={{ backgroundColor: scorer.teamColor }}
                      />
                      {scorer.teamName}
                    </div>
                  </div>
                  
                  <div className="font-bold text-lg">
                    {scorer.goals}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
      
      {activeTab === 'bracket' && bracket && (
        <Card>
          <h2 className="text-lg font-semibold mb-3 flex items-center">
            <TrendingUp size={20} className="mr-1" />
            Tournament Bracket
          </h2>
          
          <div className="overflow-x-auto pb-2">
            <div className="flex space-x-4 min-w-max">
              {bracket.rounds.map((round, roundIndex) => (
                <div key={round.name} className="flex flex-col">
                  <h3 className="text-sm font-medium mb-3 text-center">
                    {round.name.startsWith('knockout-')
                      ? `Round ${round.name.replace('knockout-', '')}`
                      : round.name.startsWith('round-') && round.name !== 'round-robin'
                        ? `Round ${round.name.replace('round-', '')}`
                        : round.name}
                  </h3>
                  
                  <div className="flex flex-col space-y-4">
                    {round.matches.map((match, matchIndex) => (
                      <div 
                        key={match.id}
                        className={`border dark:border-gray-700 rounded-lg p-3 w-56 ${
                          match.status === 'completed' 
                            ? 'bg-green-50 dark:bg-green-900/10' 
                            : match.status === 'in-progress'
                              ? 'bg-blue-50 dark:bg-blue-900/10'
                              : ''
                        }`}
                      >
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                          Match {matchIndex + 1}
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-1 truncate max-w-[100px]">
                              {match.homeTeam && (
                                <>
                                  <div 
                                    className="w-2 h-2 rounded-full flex-shrink-0" 
                                    style={{ backgroundColor: match.homeTeam?.color || '#ccc' }}
                                  />
                                  <span className="truncate">{match.homeTeam?.name || 'TBD'}</span>
                                </>
                              )}
                              {!match.homeTeam && <span className="text-gray-400">TBD</span>}
                            </div>
                            
                            <div className="font-medium">
                              {match.status !== 'scheduled' ? match.homeScore : ''}
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-1 truncate max-w-[100px]">
                              {match.awayTeam && (
                                <>
                                  <div 
                                    className="w-2 h-2 rounded-full flex-shrink-0" 
                                    style={{ backgroundColor: match.awayTeam?.color || '#ccc' }}
                                  />
                                  <span className="truncate">{match.awayTeam?.name || 'TBD'}</span>
                                </>
                              )}
                              {!match.awayTeam && <span className="text-gray-400">TBD</span>}
                            </div>
                            
                            <div className="font-medium">
                              {match.status !== 'scheduled' ? match.awayScore : ''}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}
      
      {/* Share button */}
      <div className="mt-6 flex justify-center">
        <Button
          tertiary
          icon={<Share2 size={16} />}
          onClick={() => {
            // In a real app, implement sharing functionality
            alert('Sharing feature would be implemented here');
          }}
        >
          Share Leaderboard
        </Button>
      </div>
    </div>
  );
};

export default LeaderboardPage;
